import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { formatRp } from "@/lib/constants";
import AddressPicker from "@/components/AddressPicker";
import { EntityPhoto } from "@/components/EntityPhoto";
import { PrivacyConsentBox, DataSafeNote, CONSENT_VERSION } from "@/components/PrivacyConsent";
import { Package, Plus, Trash, CheckCircle, CaretLeft, Storefront, House, User, Clock, Warning } from "@phosphor-icons/react";

const MULTI_MIN_MSG = "Mohon maaf Bunda, menu ini khusus untuk pemesanan lebih dari satu layanan. Untuk satu layanan, silakan reservasi lewat menu Pilih Layanan ya.";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-[#7C4DE0] focus:ring-2 focus:ring-[#7C4DE0]/15";
const lbl = "mb-1.5 block text-xs font-semibold text-slate-600";

const HARI = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const formatLongID = (d) => {
  if (!d) return "";
  const dt = new Date(`${d}T00:00:00`);
  if (isNaN(dt)) return "";
  return `${HARI[dt.getDay()]}, ${dt.getDate()} ${BULAN[dt.getMonth()]} ${dt.getFullYear()}`;
};
const toMin = (t) => { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const toHHMM = (x) => `${String(Math.floor(x / 60) % 24).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

function ItemRow({ index, item, items, order, services, staffList, onChange, onRemove, canRemove }) {
  const [hcStaff, setHcStaff] = useState(null); // Homecare eligible staff (area-filtered)
  const [avail, setAvail] = useState(null);     // null | "loading" | {available:[]}

  const svc = services.find((s) => s.id === item.service_id) || null;
  const dur = Number(item.durasi) || 60;

  // Service -> Homecare eligible staff (allowed_petugas + area coverage)
  useEffect(() => {
    if (order.lokasi !== "Homecare" || !item.service_id) { setHcStaff(null); return; }
    axios.get(`${API}/public/homecare-staff`, { params: { service_id: item.service_id, kelurahan: order.kelurahan || undefined } })
      .then((r) => setHcStaff(r.data))
      .catch(() => setHcStaff([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.lokasi, item.service_id, order.kelurahan]);

  // Staff + date + lokasi -> real availability (SAMA seperti reservasi tunggal, termasuk jam praktik).
  // Guard respons basi (ignore) agar respons lama tak menimpa pilihan terbaru.
  useEffect(() => {
    if (!item.petugas_nama || !order.tanggal || !item.service_id) { setAvail(null); return; }
    setAvail("loading");
    let ignore = false;
    axios.get(`${API}/public/availability`, { params: { petugas: item.petugas_nama, date: order.tanggal, service_id: item.service_id, lokasi: order.lokasi } })
      .then((r) => { if (!ignore) setAvail(r.data); })
      .catch(() => { if (!ignore) setAvail({ available: [] }); });
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.petugas_nama, order.tanggal, item.service_id, order.lokasi]);

  // Staff options: Gerai -> service.allowed_petugas; Homecare -> area-filtered list
  const staffOptions = useMemo(() => {
    if (order.lokasi === "Homecare") return hcStaff || [];
    const allow = svc?.allowed_petugas || [];
    return allow.length ? staffList.filter((s) => allow.includes(s.nama)) : staffList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.lokasi, hcStaff, svc, staffList]);

  // Intra-order guidance: hide slots that clash with SIBLING items using the SAME staff
  const siblingIv = useMemo(() => items
    .map((o, idx) => (idx !== index && o.petugas_nama && o.petugas_nama === item.petugas_nama && o.jam_kunjungan
      ? { s: toMin(o.jam_kunjungan), e: toMin(o.jam_kunjungan) + (Number(o.durasi) || 60) } : null))
    .filter(Boolean), [items, index, item.petugas_nama]);

  const slots = useMemo(() => {
    const list = (avail && avail !== "loading" ? avail.available : []) || [];
    return list.filter((t) => { const s = toMin(t), e = s + dur; return !siblingIv.some((iv) => s < iv.e && iv.s < e); });
  }, [avail, siblingIv, dur]);

  // Clear a stale time selection when it is no longer offered
  useEffect(() => {
    if (item.jam_kunjungan && avail && avail !== "loading" && !slots.includes(item.jam_kunjungan)) {
      onChange({ jam_kunjungan: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, avail]);

  const pickService = (id) => {
    const s = services.find((x) => x.id === id);
    const price = order.lokasi === "Homecare" && s?.harga_homecare != null ? s.harga_homecare : (s?.harga_normal || 0);
    onChange({ service_id: id, jenis_layanan: s?.nama || "", harga: price, durasi: s?.durasi || 60, petugas_nama: "", jam_kunjungan: "" });
  };

  const locReady = !!order.lokasi;
  const needAddr = order.lokasi === "Homecare" && !order.kelurahan;

  return (
    <div data-testid={`pm-item-${index}`} className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="rounded-full bg-[#EEE6FF] px-2.5 py-0.5 text-xs font-bold text-[#6A38C7]">Layanan #{index + 1}</span>
        {canRemove && <button type="button" data-testid={`pm-remove-${index}`} onClick={onRemove} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-500"><Trash size={13} /> Hapus</button>}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* 3. Service (filtered by location) */}
        <div className="sm:col-span-2">
          <label className={lbl}>Layanan {locReady ? `(${order.lokasi === "Homecare" ? "Homecare" : "Gerai/Klinik"})` : ""} *</label>
          {!locReady ? (
            <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Pilih lokasi terlebih dahulu untuk melihat layanan yang tersedia.</p>
          ) : (
            <select data-testid={`pm-service-${index}`} value={item.service_id} onChange={(e) => pickService(e.target.value)} className={inp}>
              <option value="">-- Pilih Layanan --</option>
              {services.map((s) => <option key={s.id} value={s.id}>{`${s.nama} · ${formatRp(order.lokasi === "Homecare" && s.harga_homecare != null ? s.harga_homecare : s.harga_normal)}`}</option>)}
            </select>
          )}
        </div>

        {/* 4. Patient */}
        <div>
          <label className={lbl}>Nama Pasien / Penerima *</label>
          <input data-testid={`pm-pasien-${index}`} value={item.nama_pasien} onChange={(e) => onChange({ nama_pasien: e.target.value })} placeholder="Nama pasien" className={inp} />
        </div>

        {/* 5. Eligible staff */}
        <div>
          <label className={lbl}>Petugas / Bidan *</label>
          {!item.service_id ? (
            <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Pilih layanan dulu.</p>
          ) : needAddr ? (
            <p data-testid={`pm-need-addr-${index}`} className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Lengkapi alamat (hingga Kelurahan) untuk melihat petugas yang melayani wilayah Anda.</p>
          ) : order.lokasi === "Homecare" && hcStaff !== null && hcStaff.length === 0 ? (
            <p data-testid={`pm-no-staff-${index}`} className="rounded-xl bg-rose-50 px-3 py-2.5 text-xs font-medium text-rose-600">Belum ada petugas Homecare untuk wilayah/ layanan ini.</p>
          ) : (
            <div>
              <div data-testid={`pm-staff-cards-${index}`} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {staffOptions.map((s) => { const sel = item.petugas_nama === s.nama; return (
                  <button type="button" key={s.id} data-testid={`pm-staff-card-${index}-${s.id}`} onClick={() => onChange({ petugas_nama: s.nama, jam_kunjungan: "" })} className={`flex items-center gap-2 rounded-xl border p-2 text-left transition-all ${sel ? "border-[#7C4DE0] bg-[#F5F0FE] ring-2 ring-[#7C4DE0]/25" : "border-slate-200 bg-white hover:border-[#7C4DE0]"}`}>
                    <EntityPhoto src={s.foto_file_id ? `${API}/public/staff/${s.id}/foto` : null} name={s.nama} rounded="rounded-full" className="h-9 w-9 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-bold text-slate-800">{s.nama}</p>
                      {(s.masa_kerja_text || s.pengalaman_text) && <p className="truncate text-[10px] text-slate-500">{[s.masa_kerja_text, s.pengalaman_text].filter(Boolean).join(" · ")}</p>}
                    </div>
                    {sel && <CheckCircle size={16} weight="fill" className="shrink-0 text-[#7C4DE0]" />}
                  </button>
                ); })}
              </div>
              <select data-testid={`pm-staff-${index}`} value={item.petugas_nama} onChange={(e) => onChange({ petugas_nama: e.target.value, jam_kunjungan: "" })} className="sr-only" aria-hidden="true" tabIndex={-1}>
                <option value="">-- Pilih Petugas --</option>
                {staffOptions.map((s) => <option key={s.id} value={s.nama}>{s.nama}{s.jenis ? ` (${s.jenis})` : ""}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* 6. Available time slots (never free-text) */}
        <div className="sm:col-span-2">
          <label className={lbl}>Jam Tersedia *</label>
          {!item.petugas_nama || !order.tanggal ? (
            <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Pilih petugas & tanggal untuk melihat jam yang tersedia.</p>
          ) : avail === "loading" ? (
            <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Memuat jam tersedia...</p>
          ) : slots.length === 0 ? (
            <p data-testid={`pm-no-slots-${index}`} className="rounded-xl bg-rose-50 px-3 py-2.5 text-xs font-medium text-rose-600">Tidak ada jadwal tersedia untuk petugas ini pada tanggal tersebut. Silakan pilih tanggal atau petugas lain.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid={`pm-slots-${index}`}>
              {slots.map((t) => (
                <button key={t} type="button" data-testid={`pm-slot-${index}-${t}`} onClick={() => onChange({ jam_kunjungan: t })}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${item.jam_kunjungan === t ? "border-[#7C4DE0] bg-[#EEE6FF] text-[#6A38C7]" : "border-slate-200 text-slate-600 hover:border-[#7C4DE0]"}`}>{t}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected summary */}
      {item.service_id && item.jam_kunjungan && (
        <div data-testid={`pm-summary-${index}`} className="mt-3 rounded-xl bg-[#FAF7FF] px-3 py-2 text-xs text-slate-600">
          <span className="font-bold text-slate-800">{item.jenis_layanan}</span>
          {item.nama_pasien ? ` · ${item.nama_pasien}` : ""}
          {item.petugas_nama ? <span className="inline-flex items-center gap-1"> · <User size={11} /> {item.petugas_nama}</span> : ""}
          <span className="inline-flex items-center gap-1"> · <Clock size={11} /> {item.jam_kunjungan}–{toHHMM(toMin(item.jam_kunjungan) + dur)} ({dur} mnt)</span>
          <span className="font-bold text-[#7C4DE0]"> · {formatRp(item.harga)}</span>
        </div>
      )}
    </div>
  );
}

export default function PublicMultiBooking() {
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [locked, setLocked] = useState(false);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [error, setError] = useState("");
  const [f, setF] = useState({ nama_kontak: "", no_whatsapp: "", lokasi: "", tanggal: "" });
  const [items, setItems] = useState([{ service_id: "", jenis_layanan: "", petugas_nama: "", jam_kunjungan: "", durasi: 60, harga: 0, nama_pasien: "" }]);

  useEffect(() => {
    axios.get(`${API}/public/settings`).then(({ data }) => setLocked(!!data.booking_locked)).catch(() => {});
    axios.get(`${API}/public/services`).then(({ data }) => setServices(Array.isArray(data) ? data : (data.data || []))).catch(() => {});
    axios.get(`${API}/public/staff`).then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  // 2. Location filters services
  const availServices = useMemo(() => services.filter((s) => f.lokasi === "Homecare" ? !!s.bisa_homecare : f.lokasi === "Gerai" ? s.di_gerai !== false : false), [services, f.lokasi]);

  // Location change: clear now-incompatible services and warn
  const chooseLokasi = (loc) => {
    setF((s) => ({ ...s, lokasi: loc }));
    setItems((arr) => arr.map((it) => {
      if (!it.service_id) return it;
      const svc = services.find((s) => s.id === it.service_id);
      const ok = loc === "Homecare" ? !!svc?.bisa_homecare : svc?.di_gerai !== false;
      if (!ok) {
        toast.warning(`"${it.jenis_layanan}" tidak tersedia untuk ${loc === "Homecare" ? "Homecare" : "Gerai/Klinik"} — dihapus.`);
        return { ...it, service_id: "", jenis_layanan: "", harga: 0, durasi: 60, petugas_nama: "", jam_kunjungan: "" };
      }
      // re-price for new location
      const price = loc === "Homecare" && svc?.harga_homecare != null ? svc.harga_homecare : (svc?.harga_normal || 0);
      return { ...it, harga: price, petugas_nama: "", jam_kunjungan: "" };
    }));
  };

  const addItem = () => setItems((arr) => [...arr, { service_id: "", jenis_layanan: "", petugas_nama: "", jam_kunjungan: "", durasi: 60, harga: 0, nama_pasien: "" }]);
  const removeItem = (i) => setItems((arr) => arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr);

  const subtotal = items.reduce((s, it) => s + Number(it.harga || 0), 0);
  const picked = items.filter((it) => it.service_id);
  const tooFew = picked.length < 2;

  const submit = async () => {
    setError("");
    if (!privacyOk) return setError("Mohon centang persetujuan Kebijakan Privasi.");
    if (tooFew) return setError(MULTI_MIN_MSG);
    if (!f.nama_kontak || !f.no_whatsapp) return setError("Nama kontak dan No. WhatsApp wajib diisi.");
    if (!f.lokasi) return setError("Pilih lokasi layanan (Gerai/Klinik atau Homecare).");
    if (!f.tanggal) return setError("Pilih tanggal kunjungan.");
    if (f.lokasi === "Homecare" && !f.kelurahan) return setError("Alamat Homecare (hingga Kelurahan/Desa) wajib dilengkapi.");
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.service_id) return setError(`Layanan #${i + 1}: pilih layanan.`);
      if (!it.nama_pasien) return setError(`Layanan #${i + 1}: isi nama pasien.`);
      if (!it.petugas_nama) return setError(`Layanan #${i + 1}: pilih petugas.`);
      if (!it.jam_kunjungan) return setError(`Layanan #${i + 1}: pilih jam tersedia.`);
    }
    setBusy(true);
    try {
      const payload = {
        nama_kontak: f.nama_kontak, no_whatsapp: f.no_whatsapp, lokasi: f.lokasi, alamat: f.alamat || "", tanggal: f.tanggal,
        consent_privacy: privacyOk, consent_version: CONSENT_VERSION,
        items: items.map((it) => ({ jenis_layanan: it.jenis_layanan, service_id: it.service_id, petugas_nama: it.petugas_nama, jam_kunjungan: it.jam_kunjungan, durasi: Number(it.durasi) || 60, harga: Number(it.harga) || 0, nama_pasien: it.nama_pasien, kelurahan: f.kelurahan || "" })),
      };
      const { data } = await axios.post(`${API}/public/orders`, payload);
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // Server-side final validation is the source of truth — surface which item failed.
      setError(e?.response?.data?.detail || "Gagal membuat pesanan. Silakan coba lagi.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7FF]">
      <header className="border-b border-rose-100/70 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Package size={26} weight="duotone" className="text-[#7C4DE0]" />
          <p className="font-heading text-sm font-extrabold text-slate-800">Pesan Beberapa Layanan</p>
          <Link to="/booking" className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[#DB2777]"><CaretLeft size={14} /> Satu Layanan</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {locked ? (
          <div data-testid="multi-maintenance" className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
            <h1 className="font-heading text-xl font-extrabold text-slate-800">Reservasi Online Sedang Dipelihara</h1>
            <p className="mt-2 text-slate-600">Silakan hubungi Admin GBS untuk reservasi.</p>
          </div>
        ) : done ? (
          <div data-testid="multi-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
            <CheckCircle size={52} weight="fill" className="mx-auto text-emerald-500" />
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-slate-800">Pesanan Berhasil!</h1>
            <p className="mt-1 text-slate-600">No. Pesanan <b>{done.order_no}</b> · {done.items?.length} layanan</p>
            <p className="mt-2 text-lg font-extrabold text-[#7C4DE0]">Estimasi {formatRp(done.grand_total)}</p>
            <p className="mt-2 text-sm text-slate-500">Pesanan berhasil dibuat dan sedang menunggu konfirmasi Admin. Setelah tagihan dikonfirmasi, link pembayaran akan dikirim ke WhatsApp Anda.</p>
            <button data-testid="multi-again" onClick={() => nav("/")} className="mt-5 rounded-xl bg-[#7C4DE0] px-6 py-2.5 text-sm font-bold text-white">Selesai</button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Contact + Location + Date (shared) */}
            <div className="rounded-2xl border border-[#E0D2FF] bg-white p-4">
              <p className="mb-3 font-heading font-bold text-slate-800">Kontak & Lokasi</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><label className={lbl}>Nama Kontak Utama *</label><input data-testid="pm-kontak" value={f.nama_kontak} onChange={(e) => set("nama_kontak", e.target.value)} placeholder="Nama lengkap" className={inp} /></div>
                <div><label className={lbl}>No. WhatsApp *</label><input data-testid="pm-wa" value={f.no_whatsapp} onChange={(e) => set("no_whatsapp", e.target.value)} placeholder="0812xxxxxxxx" className={inp} /></div>
                <div className="sm:col-span-2 -mt-2"><DataSafeNote /></div>

                <div className="sm:col-span-2">
                  <label className={lbl}>Lokasi Layanan *</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" data-testid="pm-loc-gerai" onClick={() => chooseLokasi("Gerai")} className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold ${f.lokasi === "Gerai" ? "border-[#7C4DE0] bg-[#EEE6FF] text-[#6A38C7]" : "border-slate-200 text-slate-500"}`}><Storefront size={18} /> Gerai / Klinik</button>
                    <button type="button" data-testid="pm-loc-homecare" onClick={() => chooseLokasi("Homecare")} className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold ${f.lokasi === "Homecare" ? "border-[#7C4DE0] bg-[#EEE6FF] text-[#6A38C7]" : "border-slate-200 text-slate-500"}`}><House size={18} /> Homecare</button>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label className={lbl}>Tanggal Kunjungan *</label>
                  <input data-testid="pm-tanggal" type="date" min={todayStr()} value={f.tanggal} onChange={(e) => { const v = e.target.value; set("tanggal", v); setItems((arr) => arr.map((it) => ({ ...it, jam_kunjungan: "" }))); }} className={inp} />
                  <p data-testid="pm-tanggal-preview" className={`mt-1.5 text-sm font-semibold ${f.tanggal ? "text-[#6A38C7]" : "text-slate-400"}`}>{f.tanggal ? formatLongID(f.tanggal) : "Pilih tanggal"}</p>
                </div>

                {f.lokasi === "Homecare" && (
                  <div className="sm:col-span-2">
                    <p className="mb-1.5 text-xs font-semibold text-slate-600">Alamat Homecare (dipakai untuk semua layanan) *</p>
                    <AddressPicker value={f} onPatch={(p) => setF((s) => ({ ...s, ...p }))} testidPrefix="pm-addr" />
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="rounded-2xl border border-[#E0D2FF] bg-white p-4">
              <p className="mb-3 font-heading font-bold text-slate-800">Layanan / Pasien</p>
              <div className="space-y-3">
                {items.map((it, i) => (
                  <ItemRow key={i} index={i} item={it} items={items} order={f} services={availServices} staffList={staff}
                    onChange={(patch) => setItem(i, patch)} onRemove={() => removeItem(i)} canRemove={items.length > 1} />
                ))}
                <button type="button" data-testid="pm-add-item" onClick={addItem} disabled={!f.lokasi} className="inline-flex items-center gap-1.5 rounded-xl bg-[#EEE6FF] px-3 py-2 text-xs font-bold text-[#6A38C7] disabled:opacity-50"><Plus size={14} weight="bold" /> Tambah Layanan</button>
              </div>
            </div>

            {/* Summary + submit */}
            <div className="rounded-2xl border border-[#E0D2FF] bg-[#FAF7FF] p-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal layanan</span><span data-testid="pm-subtotal" className="font-bold text-slate-800">{formatRp(subtotal)}</span></div>
              {f.lokasi === "Homecare" && <p className="mt-1 flex items-start gap-1 text-xs text-amber-600"><Warning size={13} className="mt-0.5" /> Biaya kunjungan Homecare (1x per pesanan) & diskon ditentukan Admin saat konfirmasi tagihan.</p>}
              {error && <p data-testid="pm-error" className="mt-3 rounded-lg bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-600">{error}</p>}
              {tooFew && (
                <div data-testid="pm-min-two-note" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  <p>{MULTI_MIN_MSG}</p>
                  <Link data-testid="pm-single-booking-link" to={picked[0] ? `/booking?service=${picked[0].service_id}` : "/booking"} className="mt-2 inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 font-bold text-[#DB2777] ring-1 ring-[#F9C6DD]">Reservasi satu layanan</Link>
                </div>
              )}
              <div className="mt-3"><PrivacyConsentBox checked={privacyOk} onChange={setPrivacyOk} testid="pm-privacy-consent" /></div>
              <button data-testid="pm-submit" onClick={submit} disabled={busy || tooFew || !privacyOk} className="mt-3 w-full rounded-xl bg-[#7C4DE0] px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{busy ? "Memproses..." : "Kirim Pesanan"}</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
